// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/MissionLaunchPage.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './MissionLaunch.css';
import MissionProgress from './MissionProgress';
import WelcomeMission from './WelcomeMission';
import OrganizationSetup from './OrganizationSetup';
import FrameworkIntelligence from './FrameworkIntelligence';
import ConnectorCatalog from './ConnectorCatalog';
import ConnectorSetupWizard from '../enterprise-admin/connectors/setup/ConnectorSetupWizard';
import MissionCard from './shared/MissionCard';
import MissionScan from './MissionScan';
import ExecutiveMissionBrief from './ExecutiveMissionBrief';
import OperationalReadiness from './OperationalReadiness';
import MissionReady from './MissionReady';

const steps = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'organization', label: 'Organization' },
  { key: 'frameworks', label: 'Frameworks' },
  { key: 'connectors', label: 'Connectors' },
  { key: 'scan', label: 'Mission Scan' },
  { key: 'brief', label: 'Brief' },
  { key: 'readiness', label: 'Readiness' },
  { key: 'ready', label: 'Mission Ready' },
];

const CONNECTOR_FOCUS_REFRESH_COOLDOWN_MS = 30000;
const SCAN_POLL_INTERVAL_MS = 1000;
const MISSION_REQUEST_TIMEOUT_MS = 30000;

const defaultOrganization = {
  organization_name: '',
  industry: '',
  employee_count: '',
  countries: [],
  cloud_providers: [],
  critical_systems: [],
  business_objectives: [],
  audit_timeline: '',
};

const connectorCatalog = [
  {
    key: 'aws',
    provider: 'aws',
    name: 'AWS',
    category: 'Cloud',
    short: 'AWS',
    estimated_evidence_records: 0,
    mapped_controls: 0,
    coverage: 'High',
    mission_role: 'Cloud posture + IAM',
    scope: 'S3, IAM, CloudTrail, infrastructure posture',
    why_it_matters: 'Cloud posture, IAM, S3, audit trails, and infrastructure evidence.',
  },
  {
    key: 'google_workspace',
    provider: 'google_workspace',
    name: 'Google Workspace',
    category: 'Collaboration',
    short: 'GW',
    estimated_evidence_records: 0,
    mapped_controls: 0,
    coverage: 'Identity',
    mission_role: 'Workspace identity',
    scope: 'Admin activity, users, groups, access posture',
    why_it_matters: 'Admin activity, identity governance, access posture, and workspace controls.',
  },
  {
    key: 'okta',
    provider: 'okta',
    name: 'Okta',
    category: 'Identity',
    short: 'OK',
    estimated_evidence_records: 0,
    mapped_controls: 0,
    coverage: 'Identity',
    mission_role: 'Access governance',
    scope: 'MFA, sign-on policy, privileged users, risk events',
    why_it_matters: 'MFA, sign-on policy, privileged users, and identity risk evidence.',
  },
  {
    key: 'github',
    provider: 'github',
    name: 'GitHub',
    category: 'DevOps',
    short: 'GH',
    estimated_evidence_records: 0,
    mapped_controls: 0,
    coverage: 'Code',
    mission_role: 'SDLC governance',
    scope: 'Repository governance, branch protection, secrets posture',
    why_it_matters: 'Repository governance, branch protection, secrets posture, and SDLC evidence.',
  },
  {
    key: 'cloudflare',
    provider: 'cloudflare',
    name: 'Cloudflare',
    category: 'Security',
    short: 'CF',
    estimated_evidence_records: 0,
    mapped_controls: 0,
    coverage: 'Edge',
    mission_role: 'Edge protection',
    scope: 'WAF, DNS, edge security, perimeter evidence',
    why_it_matters: 'WAF, DNS, edge security, application protection, and perimeter evidence.',
  },
];

function normalizeProvider(value) {
  return String(value || '').trim().toLowerCase().replace(/-/g, '_');
}

function operationState(operation = {}) {
  return String(operation.lifecycle_state || 'not_configured').trim().toLowerCase();
}

function operationIsConnected(operation = {}) {
  return ['healthy', 'connected'].includes(operationState(operation));
}

function providerDisplayName(providerKey) {
  return normalizeProvider(providerKey)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function providerShortName(providerKey, displayName) {
  const words = String(displayName || providerKey || 'CX')
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words.map((word) => word[0]).join('').slice(0, 3).toUpperCase();
}

function providerCategory(providerKey, explicitCategory = '') {
  const supplied = String(explicitCategory || '').trim();
  if (supplied) return supplied;

  const key = normalizeProvider(providerKey);
  if (key === 'aws' || key.includes('azure') || key.includes('gcp') || key.includes('google_cloud')) {
    return 'Cloud';
  }
  if (key === 'okta' || key.includes('entra') || key.includes('identity')) return 'Identity';
  if (key === 'github' || key.includes('gitlab') || key.includes('bitbucket')) return 'DevOps';
  if (key === 'google_workspace' || key.includes('microsoft_365') || key.includes('slack')) {
    return 'Collaboration';
  }
  if (key === 'cloudflare' || key.includes('security') || key.includes('network')) return 'Security';
  return 'Other';
}

function connectorFromCatalogItem(item = {}) {
  const providerKey = normalizeProvider(
    item.provider_key ||
    item.key ||
    item.provider
  );
  if (!providerKey) return null;

  const displayName =
    item.display_name ||
    item.provider_name ||
    item.label ||
    item.name ||
    providerDisplayName(providerKey);

  return {
    key: providerKey,
    provider: providerKey,
    name: displayName,
    short: item.short || item.short_name || providerShortName(providerKey, displayName),
    category: providerCategory(
      providerKey,
      item.category || item.provider_category || item.catalog_category
    ),
    estimated_evidence_records: Number(
      item.estimated_evidence_records ||
      item.evidence_estimate ||
      0
    ),
    mapped_controls: Number(item.mapped_controls || item.control_count || 0),
    coverage: item.coverage || item.coverage_label || 'Catalog',
    mission_role:
      item.mission_role ||
      item.headline ||
      item.description ||
      'Read-only evidence collection',
    scope:
      item.scope ||
      item.evidence_scope ||
      item.description ||
      'Provider-authorized read-only evidence collection',
    why_it_matters:
      item.why_it_matters ||
      item.description ||
      'Operational evidence for Mission Scan.',
    connector_id: item.connector_id || null,
    lifecycle_state: item.lifecycle_state || 'not_configured',
    connection_state: item.lifecycle_state || 'not_configured',
    authorization_status: item.authorization_status || 'not_configured',
    governance_status: item.governance_status || 'not_configured',
    health_status: item.health_status || 'not_checked',
    health_label: item.health_status === 'healthy' ? 'Healthy' : item.status_label || 'Not connected',
    status_label: item.status_label || null,
    status_detail: item.status_detail || null,
    verified_account: item.verified_account || null,
    evidence_records: 0,
    selected: false,
  };
}

function mergeConnectorCatalogState(currentConnectors = [], catalogItems = []) {
  const currentByKey = new Map(
    (Array.isArray(currentConnectors) ? currentConnectors : []).map((connector) => [
      normalizeProvider(connector.key || connector.provider),
      connector,
    ])
  );

  (Array.isArray(catalogItems) ? catalogItems : []).forEach((item) => {
    const catalogConnector = connectorFromCatalogItem(item);
    if (!catalogConnector) return;

    const existing = currentByKey.get(catalogConnector.key);
    currentByKey.set(
      catalogConnector.key,
      existing
        ? {
            ...catalogConnector,
            ...existing,
            category: existing.category || catalogConnector.category,
            mission_role: existing.mission_role || catalogConnector.mission_role,
            scope: existing.scope || catalogConnector.scope,
            why_it_matters: existing.why_it_matters || catalogConnector.why_it_matters,
            selected: existing.selected === true,
          }
        : catalogConnector
    );
  });

  return Array.from(currentByKey.values());
}

function mergeMissionConnectorIntelligence(currentConnectors = [], launchCards = []) {
  const currentByKey = new Map(
    (Array.isArray(currentConnectors) ? currentConnectors : []).map((connector) => [
      normalizeProvider(connector.key || connector.provider),
      connector,
    ])
  );

  (Array.isArray(launchCards) ? launchCards : []).forEach((card) => {
    const key = normalizeProvider(card?.key);
    if (!key) return;

    const existing = currentByKey.get(key);
    const displayName =
      card?.label ||
      existing?.name ||
      providerDisplayName(key);

    const authoritativeMetadata = {
      key,
      provider: existing?.provider || key,
      name: displayName,
      short: existing?.short || providerShortName(key, displayName),
      category: providerCategory(key, card?.category || existing?.category),
      estimated_evidence_records: Number(card?.potential_evidence || 0),
      mapped_controls: Number(card?.mapped_controls || 0),
      coverage: card?.coverage || existing?.coverage || 'Catalog',
      mission_role:
        existing?.mission_role ||
        card?.why_it_matters ||
        'Read-only evidence collection',
      scope:
        existing?.scope ||
        card?.coverage ||
        card?.why_it_matters ||
        'Provider-authorized read-only evidence collection',
      why_it_matters:
        card?.why_it_matters ||
        existing?.why_it_matters ||
        'Operational evidence for Mission Scan.',
      recommended:
        card?.recommended === true ||
        card?.recommendation_type === 'recommended',
    };

    currentByKey.set(
      key,
      existing
        ? {
            ...existing,
            ...authoritativeMetadata,
            // Selection and live connection state remain separate authorities.
            selected: existing.selected === true,
            lifecycle_state: existing.lifecycle_state || 'not_configured',
            connection_state: existing.connection_state || 'not_configured',
          }
        : {
            ...authoritativeMetadata,
            connector_id: null,
            lifecycle_state: 'not_configured',
            connection_state: 'not_configured',
            authorization_status: 'not_configured',
            governance_status: 'not_configured',
            health_status: 'not_checked',
            health_label: 'Not connected',
            status_label: null,
            status_detail: null,
            verified_account: null,
            evidence_records: 0,
            selected: false,
          }
    );
  });

  return Array.from(currentByKey.values());
}

function buildConnectorLaunchState(operations = []) {
  const safeOperations = Array.isArray(operations) ? operations : [];
  const operationByProvider = new Map(
    safeOperations.map((operation) => [
      normalizeProvider(operation.provider_key),
      operation,
    ])
  );

  const knownProviders = connectorCatalog.map((meta) => {
    const operation = operationByProvider.get(meta.provider);
    operationByProvider.delete(meta.provider);

    const state = operationState(operation);
    const connected = operationIsConnected(operation);
    const summary = operation?.last_successful_collection_summary || {};

    return {
      ...meta,
      connector_id: operation?.connector_id || null,
      lifecycle_state: state,
      connection_state: state,
      authorization_status: operation?.authorization_status || 'not_configured',
      governance_status: operation?.governance_status || 'not_configured',
      health_status: operation?.health_status || 'not_checked',
      health_label: connected
        ? (operation?.health_status === 'healthy' ? 'Healthy' : 'Connected')
        : operation?.status_label || 'Pending',
      status_label: operation?.status_label || null,
      status_detail: operation?.status_detail || null,
      verified_account: operation?.verified_account || null,
      evidence_records:
        Number(summary.records_stored || 0) ||
        Number(summary.events_stored || 0) ||
        Number(summary.repositories_stored || 0) ||
        Number(summary.bucket_count || 0),
      selected: false,
    };
  });

  const futureProviders = Array.from(operationByProvider.values()).map((operation) => {
    const providerKey = normalizeProvider(operation.provider_key);
    const displayName = operation.provider_name || providerDisplayName(providerKey);
    const state = operationState(operation);

    return {
      key: providerKey,
      provider: providerKey,
      name: displayName,
      short: providerShortName(providerKey, displayName),
      estimated_evidence_records: 0,
      mapped_controls: 0,
      coverage: 'Catalog',
      mission_role: operation.description || 'Read-only evidence collection',
      scope: operation.description || 'Provider-authorized read-only evidence collection',
      why_it_matters: operation.description || 'Operational evidence for Mission Scan.',
      connector_id: operation.connector_id || null,
      lifecycle_state: state,
      connection_state: state,
      authorization_status: operation.authorization_status || 'not_configured',
      governance_status: operation.governance_status || 'not_configured',
      health_status: operation.health_status || 'not_checked',
      health_label: operation.health_status === 'healthy' ? 'Healthy' : operation.status_label || 'Pending',
      status_label: operation.status_label || null,
      status_detail: operation.status_detail || null,
      verified_account: operation.verified_account || null,
      evidence_records: 0,
      selected: false,
    };
  });

  return [...knownProviders, ...futureProviders];
}

function routeStepIndex(routeParams = {}) {
  const requested = String(routeParams.step || routeParams.missionStep || '')
    .trim()
    .toLowerCase();
  const index = steps.findIndex((step) => step.key === requested);
  return index >= 0 ? index : 0;
}

function stepIndex(stepKey) {
  const index = steps.findIndex((step) => step.key === String(stepKey || '').trim().toLowerCase());
  return index >= 0 ? index : 0;
}

function errorMessage(payload, fallback) {
  const detail = payload?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  if (detail) return JSON.stringify(detail);
  return fallback;
}

function scrollMissionLaunchToTop(stageRef) {
  if (typeof window === 'undefined') return;

  const doScroll = () => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }

    const scrollTargets = [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.querySelector('#root'),
      document.querySelector('.App'),
      document.querySelector('main'),
      document.querySelector('.mission-launch-page'),
    ];

    scrollTargets.forEach((target) => {
      if (!target) return;
      try {
        target.scrollTop = 0;
        target.scrollLeft = 0;
      } catch {
        // Ignore non-scrollable nodes.
      }
    });

    try {
      stageRef.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
    } catch {
      // Ignore older browser scroll behavior quirks.
    }
  };

  doScroll();
  window.requestAnimationFrame(() => {
    doScroll();
    window.requestAnimationFrame(doScroll);
  });
}

export default function MissionLaunchPage({
  apiBaseUrl = '',
  getAccessToken,
  tenantId = '',
  userId = '',
  userProfile = null,
  routeParams = {},
  setMode,
  onModeChange,
  onGoToMissionControl,
}) {
  const initialStepIndex = routeStepIndex(routeParams);
  const [activeStep, setActiveStep] = useState(initialStepIndex);
  const briefEnteredRef = useRef(false);
  const [highestReachedStep, setHighestReachedStep] = useState(initialStepIndex);
  const [organization, setOrganization] = useState(defaultOrganization);
  const [frameworkIntelligence, setFrameworkIntelligence] = useState({
    recommended_frameworks: [],
    optional_frameworks: [],
  });
  const [recommendedConnectorKeys, setRecommendedConnectorKeys] = useState([]);
  const [connectors, setConnectors] = useState(() => buildConnectorLaunchState([]));
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [connectorError, setConnectorError] = useState('');
  const [connectorSetupOpen, setConnectorSetupOpen] = useState(false);
  const [connectorSetupProviderKey, setConnectorSetupProviderKey] = useState(null);
  const [connectorSetupReconnectMode, setConnectorSetupReconnectMode] = useState(false);
  const [connectorSetupCatalog, setConnectorSetupCatalog] = useState(null);
  const [connectorSetupUsers, setConnectorSetupUsers] = useState([]);
  const [connectorSetupLoading, setConnectorSetupLoading] = useState(false);
  const [connectorSetupSaving, setConnectorSetupSaving] = useState(false);
  const [connectorSetupError, setConnectorSetupError] = useState('');
  const [launchFinalizing, setLaunchFinalizing] = useState(false);
  const [missionLoading, setMissionLoading] = useState(false);
  const [missionSaving, setMissionSaving] = useState(false);
  const [missionError, setMissionError] = useState('');
  const [scanStatus, setScanStatus] = useState('not_started');
  const [scanRuntime, setScanRuntime] = useState({
    status: 'not_started',
    progress: 0,
    stage: 'not_started',
  });
  const [scanError, setScanError] = useState('');
  const [scanAttempts, setScanAttempts] = useState([]);
  const [unifiedAwsProgress, setUnifiedAwsProgress] = useState(null);
  const [scanRetrying, setScanRetrying] = useState(false);
  const [scanStateVersion, setScanStateVersion] = useState(null);
  const [scanTransitionVersion, setScanTransitionVersion] = useState(null);
  const [executiveBrief, setExecutiveBrief] = useState({});
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState('');
  const [readiness, setReadiness] = useState({});
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState('');
  const [completionError, setCompletionError] = useState('');
  const [stepStatuses, setStepStatuses] = useState({});

  const stageRef = useRef(null);
  const connectorRequestInFlightRef = useRef(false);
  const connectorStateLoadedRef = useRef(false);
  const connectorSelectionDirtyRef = useRef(false);
  const lastFocusRefreshAtRef = useRef(0);
  const connectorSetupRequestControllersRef = useRef(new Set());
  const scanPollInFlightRef = useRef(false);
  const scanStartInFlightRef = useRef(false);
  const scanRetryInFlightRef = useRef(false);
  const missionRequestControllersRef = useRef(new Set());

  const navigate = setMode || onModeChange;

  const progress = useMemo(
    () => Math.round((highestReachedStep / (steps.length - 1)) * 100),
    [highestReachedStep]
  );

  const isMissionReadyStep = steps[activeStep]?.key === 'ready';

  const missionRequest = useCallback(async (path, options = {}) => {
    if (!apiBaseUrl || !tenantId || !userId) {
      throw new Error('Mission Launch requires tenant and user context.');
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      MISSION_REQUEST_TIMEOUT_MS
    );
    missionRequestControllersRef.current.add(controller);

    try {
      const token = getAccessToken ? await getAccessToken() : null;
      if (!token) {
        throw new Error('Authentication token unavailable.');
      }

      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId,
          'X-User-Name':
            userProfile?.name ||
            userProfile?.email ||
            'OASYS Administrator',
          ...(options.headers || {}),
        },
      });

      const responseText = await response.text();
      let payload = {};

      if (responseText) {
        try {
          payload = JSON.parse(responseText);
        } catch {
          payload = { detail: responseText };
        }
      }

      if (!response.ok) {
        throw new Error(
          errorMessage(
            payload,
            `Mission Launch request failed (${response.status})`
          )
        );
      }

      return payload;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error(
          'Mission Launch request timed out. Your durable progress is preserved; refresh the status and try again.'
        );
      }

      if (
        error instanceof TypeError &&
        /failed to fetch|network/i.test(String(error?.message || error))
      ) {
        throw new Error(
          'Mission Launch could not reach the OASYS API. Your durable progress is preserved; verify the API is available and refresh the status.'
        );
      }

      throw error;
    } finally {
      window.clearTimeout(timeoutId);
      missionRequestControllersRef.current.delete(controller);
    }
  }, [
    apiBaseUrl,
    getAccessToken,
    tenantId,
    userId,
    userProfile?.email,
    userProfile?.name,
  ]);

  const syncPersistedMissionState = useCallback((payload = {}, { navigateToCurrent = true } = {}) => {
    const persistedState = payload?.state || payload;
    const session = persistedState?.session || {};
    const stepList = persistedState?.steps || payload?.steps || [];
    const currentStepKey = session.current_step || payload.current_phase;

    if (Array.isArray(stepList)) {
      setStepStatuses(
        stepList.reduce((accumulator, step) => {
          const key = String(step?.step_key || '').trim().toLowerCase();
          if (key) {
            accumulator[key] = String(step?.status || 'not_started')
              .trim()
              .toLowerCase();
          }
          return accumulator;
        }, {})
      );
    }
    const highestStepKey = session.highest_completed_step;

    const organizationPayload =
      session.organization_payload && Object.keys(session.organization_payload).length
        ? session.organization_payload
        : payload?.organization_profile || {};

    if (organizationPayload && Object.keys(organizationPayload).length) {
      setOrganization((current) => ({
        ...current,
        ...organizationPayload,
      }));
    }

    if (payload?.framework_intelligence && typeof payload.framework_intelligence === 'object') {
      setFrameworkIntelligence(payload.framework_intelligence);
    }

    const connectorLaunch = payload?.connector_launch || {};
    if (Array.isArray(connectorLaunch.launch_cards)) {
      setRecommendedConnectorKeys(
        connectorLaunch.launch_cards
          .filter((connector) => (
            connector?.recommended === true ||
            connector?.recommendation_type === 'recommended'
          ))
          .map((connector) => normalizeProvider(connector.key))
          .filter(Boolean)
      );

      setConnectors((current) => mergeMissionConnectorIntelligence(
        current,
        connectorLaunch.launch_cards
      ));
    }

    if (
      Array.isArray(session.connector_selection) &&
      !connectorSelectionDirtyRef.current
    ) {
      const selectedKeys = new Set(session.connector_selection.map(normalizeProvider));
      setConnectors((current) => current.map((connector) => ({
        ...connector,
        selected: selectedKeys.has(normalizeProvider(connector.key)),
      })));
    }

    const scanStep = stepList.find((step) => step.step_key === 'scan');
    const attempts = payload?.attempts || persistedState?.attempts;
    if (Array.isArray(attempts)) {
      setScanAttempts(attempts);
    }
    if (scanStep) {
      const runtime = scanStep.output_snapshot?.scan_runtime || {};
      const normalizedStatus = scanStep.status === 'in_progress'
        ? 'running'
        : scanStep.status || runtime.status || 'not_started';

      setScanStatus(normalizedStatus);
      setScanRuntime({
        status: normalizedStatus,
        progress: Number(runtime.progress || (scanStep.status === 'complete' ? 100 : 0)),
        stage: runtime.stage || normalizedStatus,
        ...runtime,
      });
      setScanError(scanStep.blockers?.[0]?.message || '');
      setScanTransitionVersion(scanStep.transition_version ?? null);
    }

    if (session.state_version !== undefined) {
      setScanStateVersion(session.state_version);
    } else if (payload.state_version !== undefined) {
      setScanStateVersion(payload.state_version);
    }

    if (currentStepKey) {
      const currentIndex = stepIndex(currentStepKey);
      const highestIndex = Math.max(
        currentIndex,
        highestStepKey ? stepIndex(highestStepKey) : 0
      );
      setHighestReachedStep((current) => Math.max(current, highestIndex));
      if (navigateToCurrent) {
        setActiveStep(currentStepKey === "brief" && stepList.some((step) => step.step_key === "scan" && step.status === "complete") && !briefEnteredRef.current ? stepIndex("scan") : currentIndex);
      }
    }
  }, []);

  const loadMissionState = useCallback(async ({
    showLoading = false,
    navigateToCurrent = true,
  } = {}) => {
    if (showLoading) setMissionLoading(true);
    setMissionError('');

    try {
      const payload = await missionRequest('/mission-launch/status');
      syncPersistedMissionState(payload, { navigateToCurrent });
      return payload;
    } catch (error) {
      setMissionError(error?.message || String(error));
      return null;
    } finally {
      if (showLoading) setMissionLoading(false);
    }
  }, [missionRequest, syncPersistedMissionState]);

  const runMissionAction = useCallback(async (action) => {
    setMissionSaving(true);
    setMissionError('');

    try {
      return await action();
    } catch (error) {
      setMissionError(error?.message || String(error));
      throw error;
    } finally {
      setMissionSaving(false);
    }
  }, []);

  const loadConnectorState = useCallback(async ({ showLoading = false } = {}) => {
    if (connectorRequestInFlightRef.current) return;

    if (!apiBaseUrl || !tenantId || !userId) {
      setConnectors(buildConnectorLaunchState([]));
      setConnectorError('Connector status requires tenant and user context.');
      return;
    }

    connectorRequestInFlightRef.current = true;
    if (showLoading) setConnectorLoading(true);
    setConnectorError('');

    try {
      const token = getAccessToken ? await getAccessToken() : null;
      const response = await fetch(`${apiBaseUrl}/enterprise-admin/connectors/operations`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId,
          'X-User-Name': userProfile?.name || userProfile?.email || 'OASYS Administrator',
        },
      });

      if (!response.ok) {
        const responseText = await response.text().catch(() => '');
        throw new Error(responseText || `Connector status request failed (${response.status})`);
      }

      const payload = await response.json();
      setConnectors((current) => {
        const selectedByKey = new Map(
          current.map((connector) => [
            connector.key,
            connector.selected === true,
          ])
        );

        return mergeConnectorCatalogState(
          buildConnectorLaunchState(payload?.items).map((connector) => ({
            ...connector,
            selected: selectedByKey.has(connector.key)
              ? selectedByKey.get(connector.key)
              : false,
          })),
          current
        );
      });
      connectorStateLoadedRef.current = true;
    } catch (error) {
      setConnectors((current) => (
        current.length ? current : buildConnectorLaunchState([])
      ));
      setConnectorError(error?.message || String(error));
    } finally {
      connectorRequestInFlightRef.current = false;
      if (showLoading) setConnectorLoading(false);
    }
  }, [
    apiBaseUrl,
    getAccessToken,
    tenantId,
    userId,
    userProfile?.email,
    userProfile?.name,
  ]);

  const connectorSetupRequest = useCallback(async (path, options = {}) => {
    if (!apiBaseUrl || !tenantId || !userId) {
      throw new Error('Connector setup requires tenant and user context.');
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 20000);
    connectorSetupRequestControllersRef.current.add(controller);

    try {
      const token = getAccessToken ? await getAccessToken() : null;
      if (!token) throw new Error('Authentication token unavailable.');

      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId,
          'X-User-Name': userProfile?.name || userProfile?.email || 'OASYS Administrator',
          ...(options.headers || {}),
        },
      });

      const responseText = await response.text();
      let payload = {};

      if (responseText) {
        try {
          payload = JSON.parse(responseText);
        } catch {
          payload = { detail: responseText };
        }
      }

      if (!response.ok) {
        throw new Error(errorMessage(payload, `Connector setup request failed (${response.status})`));
      }

      return payload;
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw new Error('Connector setup request timed out or was cancelled. Please try again.');
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
      connectorSetupRequestControllersRef.current.delete(controller);
    }
  }, [
    apiBaseUrl,
    getAccessToken,
    tenantId,
    userId,
    userProfile?.email,
    userProfile?.name,
  ]);

  const runConnectorSetupAction = useCallback(async (action) => {
    setConnectorSetupSaving(true);
    setConnectorSetupError('');

    try {
      return await action();
    } catch (error) {
      setConnectorSetupError(error?.message || String(error));
      throw error;
    } finally {
      setConnectorSetupSaving(false);
    }
  }, []);

  const connectorSetupActions = useMemo(() => ({
    getConnectorSetupCatalog: () =>
      connectorSetupRequest('/enterprise-admin/connectors/setup/catalog'),
    getConnectorSetupDetail: (providerKey) =>
      connectorSetupRequest(`/enterprise-admin/connectors/setup/${providerKey}`),
    startConnectorSetup: (providerKey, payload) =>
      runConnectorSetupAction(() =>
        connectorSetupRequest(`/enterprise-admin/connectors/setup/${providerKey}/start`, {
          method: 'POST',
          body: JSON.stringify(payload || {}),
        })
      ),
    validateConnectorSetup: (providerKey, payload) =>
      runConnectorSetupAction(() =>
        connectorSetupRequest(`/enterprise-admin/connectors/setup/${providerKey}/validate`, {
          method: 'POST',
          body: JSON.stringify({ setup_inputs: payload || {} }),
        })
      ),
    completeConnectorSetup: (providerKey, payload) =>
      runConnectorSetupAction(() =>
        connectorSetupRequest(`/enterprise-admin/connectors/setup/${providerKey}/complete`, {
          method: 'POST',
          body: JSON.stringify(payload || {}),
        })
      ),
    submitConnectorRequest: (payload) =>
      runConnectorSetupAction(() =>
        connectorSetupRequest('/enterprise-admin/connectors/setup/requests', {
          method: 'POST',
          body: JSON.stringify(payload || {}),
        })
      ),
    getConnectorRequests: (limit = 100) =>
      connectorSetupRequest(`/enterprise-admin/connectors/setup/requests?limit=${limit}`),
    getConnectorRequestDetail: (requestId) =>
      connectorSetupRequest(`/enterprise-admin/connectors/setup/requests/${requestId}`),
  }), [connectorSetupRequest, runConnectorSetupAction]);

  const loadConnectorCatalog = useCallback(async () => {
    try {
      const catalog = await connectorSetupActions.getConnectorSetupCatalog();
      setConnectorSetupCatalog(catalog || null);
      setConnectors((current) => mergeConnectorCatalogState(
        current,
        catalog?.items || []
      ));
      return catalog;
    } catch (error) {
      setConnectorError((current) => (
        current || error?.message || String(error)
      ));
      return null;
    }
  }, [connectorSetupActions]);

  const loadConnectorSetupContext = useCallback(async () => {
    const [catalog, usersPayload] = await Promise.all([
      connectorSetupCatalog
        ? Promise.resolve(connectorSetupCatalog)
        : connectorSetupActions.getConnectorSetupCatalog(),
      connectorSetupRequest('/enterprise-admin/users'),
    ]);

    const operationByProvider = new Map(
      connectors.map((connector) => [connector.provider, connector])
    );

    const enrichedCatalog = {
      ...(catalog || {}),
      items: (catalog?.items || []).map((item) => {
        const operation = operationByProvider.get(item.provider_key);

        return operation
          ? {
              ...item,
              lifecycle_state: operation.lifecycle_state,
              status_label: operation.status_label,
              health_status: operation.health_status,
              connector_id: operation.connector_id,
              verified_account: operation.verified_account,
            }
          : item;
      }),
    };

    const users = usersPayload?.items || [];
    setConnectorSetupCatalog(enrichedCatalog);
    setConnectorSetupUsers(users);

    return {
      catalog: enrichedCatalog,
      users,
    };
  }, [
    connectorSetupActions,
    connectorSetupCatalog,
    connectorSetupRequest,
    connectors,
  ]);

  const openConnectorSetup = useCallback(async (providerKey = null) => {
    connectorSetupRequestControllersRef.current.forEach((controller) => controller.abort());
    connectorSetupRequestControllersRef.current.clear();

    const connector = providerKey
      ? connectors.find((item) => item.provider === providerKey)
      : null;
    const lifecycleState = String(connector?.lifecycle_state || '').toLowerCase();

    setConnectorSetupProviderKey(providerKey);
    setConnectorSetupReconnectMode(
      ['disconnected', 'reauthorization_required'].includes(lifecycleState)
    );
    setConnectorSetupError('');
    setConnectorSetupOpen(true);
    setConnectorSetupLoading(true);

    try {
      await loadConnectorSetupContext();
    } catch (error) {
      setConnectorSetupError(error?.message || String(error));
    } finally {
      setConnectorSetupLoading(false);
    }
  }, [connectors, loadConnectorSetupContext]);

  const closeConnectorSetup = useCallback(async () => {
    connectorSetupRequestControllersRef.current.forEach((controller) => controller.abort());
    connectorSetupRequestControllersRef.current.clear();

    setConnectorSetupOpen(false);
    setConnectorSetupProviderKey(null);
    setConnectorSetupReconnectMode(false);
    setConnectorSetupLoading(false);
    setConnectorSetupSaving(false);
    setConnectorSetupError('');
    await loadConnectorState({ showLoading: false });
  }, [loadConnectorState]);

  useEffect(() => () => {
    connectorSetupRequestControllersRef.current.forEach(
      (controller) => controller.abort()
    );
    connectorSetupRequestControllersRef.current.clear();
    missionRequestControllersRef.current.forEach(
      (controller) => controller.abort()
    );
    missionRequestControllersRef.current.clear();
  }, []);

  const goToStep = (stepIndexValue) => {
    if (
      stepIndexValue < 0 ||
      stepIndexValue >= steps.length ||
      (stepIndexValue > highestReachedStep && stepStatuses[steps[stepIndexValue]?.key] !== 'complete')
    ) return;
    if (stepIndexValue === stepIndex("brief")) briefEnteredRef.current = true;
    setActiveStep(stepIndexValue);
  };

  const advanceLocally = useCallback((targetStepKey) => {
    const targetIndex = stepIndex(targetStepKey);
    if (targetStepKey === "brief") briefEnteredRef.current = true;
    setActiveStep(targetIndex);
    setHighestReachedStep((highest) => Math.max(highest, targetIndex));
  }, []);

  const handleWelcomeBegin = useCallback(() => {
    advanceLocally('organization');
  }, [advanceLocally]);

  const handleOrganizationSubmit = useCallback(async () => {
    await runMissionAction(async () => {
      const payload = await missionRequest('/mission-launch/organization', {
        method: 'POST',
        body: JSON.stringify(organization),
      });
      syncPersistedMissionState(payload);
    });
  }, [
    missionRequest,
    organization,
    runMissionAction,
    syncPersistedMissionState,
  ]);

  const handleFrameworkSubmit = useCallback(async () => {
    await runMissionAction(async () => {
      const payload = await missionRequest('/mission-launch/frameworks', {
        method: 'POST',
        body: JSON.stringify({
          confirm_framework_authority: true,
        }),
      });
      syncPersistedMissionState(payload);
    });
  }, [
    missionRequest,
    runMissionAction,
    syncPersistedMissionState,
  ]);

  const handleConnectorSubmit = useCallback(async () => {
    await runMissionAction(async () => {
      const selected = connectors
        .filter((connector) => connector.selected === true)
        .map((connector) => connector.key);

      const payload = await missionRequest('/mission-launch/connectors', {
        method: 'POST',
        body: JSON.stringify({
          selected_connectors: selected,
        }),
      });

      // The server now owns the submitted selection. Allow persisted mission
      // state to hydrate it again on this response and future refreshes.
      connectorSelectionDirtyRef.current = false;
      syncPersistedMissionState(payload);
      if (payload?.initial_scan) {
        syncPersistedMissionState(payload.initial_scan, { navigateToCurrent: false });
        setScanAttempts(payload.initial_scan.attempts || []);
      }
      const blockerMessage = payload?.blockers?.[0]?.message;
      if (blockerMessage) {
        setMissionError(blockerMessage);
      }
    });
  }, [
    connectors,
    missionRequest,
    runMissionAction,
    syncPersistedMissionState,
  ]);

  const pollScanStatus = useCallback(async () => {
    if (scanPollInFlightRef.current) return null;
    scanPollInFlightRef.current = true;

    try {
      const payload = await missionRequest('/mission-launch/scan/status');
      setUnifiedAwsProgress(payload.unified_aws_progress || null);
      syncPersistedMissionState(payload, { navigateToCurrent: false });
      setScanStatus(payload.scan_status || 'not_started');
      setScanRuntime(payload.scan_runtime || {});
      setScanError(payload?.blockers?.[0]?.message || '');

      if (payload.scan_status === 'complete') {
        setHighestReachedStep((highest) => Math.max(highest, stepIndex('brief')));
      }

      return payload;
    } catch (error) {
      setScanError(error?.message || String(error));
      return null;
    } finally {
      scanPollInFlightRef.current = false;
    }
  }, [missionRequest, syncPersistedMissionState]);


  const startScan = useCallback(async () => {
    setUnifiedAwsProgress(null);
    if (scanStartInFlightRef.current) return null;

    if (scanStatus === 'running' || scanStatus === 'in_progress') {
      return pollScanStatus();
    }

    scanStartInFlightRef.current = true;
    setScanError('');

    try {
      const payload = await missionRequest('/mission-launch/scan/start', {
        method: 'POST',
        body: JSON.stringify({ requested: true }),
      });
      syncPersistedMissionState(payload, { navigateToCurrent: false });
      setScanStatus(payload.scan_status || 'running');
      setScanRuntime(
        payload.scan_runtime || {
          status: 'running',
          progress: 5,
          stage: 'initializing',
        }
      );
      return payload;
    } catch (error) {
      const message = error?.message || String(error);
      setScanError(message);
      throw error;
    } finally {
      scanStartInFlightRef.current = false;
    }
  }, [
    missionRequest,
    pollScanStatus,
    scanStatus,
    syncPersistedMissionState,
  ]);

  const retryScan = useCallback(async () => {
    setUnifiedAwsProgress(null);
    if (scanRetryInFlightRef.current) return null;

    if (scanStatus === 'running' || scanStatus === 'in_progress') {
      return pollScanStatus();
    }

    scanRetryInFlightRef.current = true;
    setScanRetrying(true);
    setScanError('');

    try {
      const payload = await missionRequest('/mission-launch/scan/retry', {
        method: 'POST',
        body: JSON.stringify({ requested: true }),
      });
      syncPersistedMissionState(payload, { navigateToCurrent: false });
      setScanStatus(payload.scan_status || 'running');
      setScanRuntime(
        payload.scan_runtime || {
          status: 'running',
          progress: 5,
          stage: 'initializing',
        }
      );
      setScanAttempts(payload.attempts || []);
      return payload;
    } catch (error) {
      const message = error?.message || String(error);
      setScanError(message);
      throw error;
    } finally {
      scanRetryInFlightRef.current = false;
      setScanRetrying(false);
    }
  }, [
    missionRequest,
    pollScanStatus,
    scanStatus,
    syncPersistedMissionState,
  ]);

  const rescanScan = useCallback(async () => {
    setUnifiedAwsProgress(null);
    if (scanStartInFlightRef.current) return null;
    scanStartInFlightRef.current = true;
    setScanError('');
    try {
      const payload = await missionRequest('/mission-launch/scan/rescan', {
        method: 'POST', body: JSON.stringify({ requested: true, expected_state_version: scanStateVersion }),
      });
      syncPersistedMissionState(payload);
      setScanAttempts(payload.attempts || []);
      return payload;
    } catch (error) {
      setScanError(error?.message || String(error));
      throw error;
    } finally {
      scanStartInFlightRef.current = false;
    }
  }, [missionRequest, scanStateVersion, syncPersistedMissionState]);

  const loadExecutiveBrief = useCallback(async () => {
    setBriefLoading(true);
    setBriefError('');

    try {
      const payload = await missionRequest('/mission-launch/executive-brief');
      setExecutiveBrief(payload.executive_brief || {});
      return payload;
    } catch (error) {
      setBriefError(error?.message || String(error));
      return null;
    } finally {
      setBriefLoading(false);
    }
  }, [missionRequest]);

  const completeExecutiveBrief = useCallback(async () => {
    await runMissionAction(async () => {
      // Reconcile Team-assisted progression before attempting a customer mutation.
      const latest = await missionRequest('/mission-launch/status');
      const latestState = latest?.state || latest;
      const latestSession = latestState?.session || {};
      const latestBrief = (latestState?.steps || latest?.steps || [])
        .find((item) => item.step_key === 'brief');
      if (latestBrief?.status === 'complete' ||
          (latestSession.current_step && latestSession.current_step !== 'brief')) {
        syncPersistedMissionState(latest);
        return;
      }
      try {
        const payload = await missionRequest('/mission-launch/executive-brief/complete', {
          method: 'POST',
          body: JSON.stringify({ explicit_review: true, review_fingerprint: executiveBrief.review_fingerprint }),
        });
        setExecutiveBrief(payload.executive_brief || executiveBrief);
        syncPersistedMissionState(payload);
      } catch (error) {
        // A concurrent Team completion may have won the race. Never resubmit it.
        if (/mission_brief_review_mismatch|Refresh and review the current verified Mission Brief/i.test(String(error?.message || ''))) {
          const refreshed = await missionRequest('/mission-launch/status');
          const refreshedState = refreshed?.state || refreshed;
          const refreshedBrief = (refreshedState?.steps || refreshed?.steps || [])
            .find((item) => item.step_key === 'brief');
          if (refreshedBrief?.status === 'complete') {
            syncPersistedMissionState(refreshed);
            return;
          }
          syncPersistedMissionState(refreshed, { navigateToCurrent: false });
          await loadExecutiveBrief();
        }
        throw error;
      }
    });
  }, [
    executiveBrief,
    loadExecutiveBrief,
    missionRequest,
    runMissionAction,
    syncPersistedMissionState,
  ]);

  const loadOperationalReadiness = useCallback(async () => {
    setReadinessLoading(true);
    setReadinessError('');

    try {
      const payload = await missionRequest('/mission-launch/readiness');
      setReadiness(payload.readiness || {});
      return payload;
    } catch (error) {
      setReadinessError(error?.message || String(error));
      return null;
    } finally {
      setReadinessLoading(false);
    }
  }, [missionRequest]);

  const completeOperationalReadiness = useCallback(async () => {
    await runMissionAction(async () => {
      const payload = await missionRequest('/mission-launch/readiness/complete', {
        method: 'POST',
        body: JSON.stringify({ explicit_review: true }),
      });
      setReadiness(payload.readiness || readiness);
      syncPersistedMissionState(payload);
    });
  }, [
    missionRequest,
    readiness,
    runMissionAction,
    syncPersistedMissionState,
  ]);

  const completeAndLaunchOasys = useCallback(async () => {
    setCompletionError('');

    try {
      const payload = await missionRequest('/mission-launch/complete', {
        method: 'POST',
        body: JSON.stringify({ explicit_completion: true }),
      });
      syncPersistedMissionState(payload, { navigateToCurrent: false });

      if (typeof onGoToMissionControl === 'function') {
        onGoToMissionControl();
        return;
      }

      if (typeof navigate === 'function') {
        navigate('mission-control');
        return;
      }

      if (typeof window !== 'undefined') {
        window.location.hash = '#mission-control';
      }
    } catch (error) {
      setCompletionError(error?.message || String(error));
      throw error;
    }
  }, [
    missionRequest,
    navigate,
    onGoToMissionControl,
    syncPersistedMissionState,
  ]);

  useEffect(() => {
    const activeKey = steps[activeStep]?.key;
    if (activeKey === 'brief') {
      loadExecutiveBrief();
    } else if (activeKey === 'readiness') {
      loadOperationalReadiness();
    }
  }, [
    activeStep,
    loadExecutiveBrief,
    loadOperationalReadiness,
  ]);

  useEffect(() => {
    scrollMissionLaunchToTop(stageRef);
    if (steps[activeStep]?.key !== 'ready') {
      setLaunchFinalizing(false);
    }
  }, [activeStep]);

  const requestedStep = routeParams?.step || routeParams?.missionStep;
  const connectorRefresh = routeParams?.connectorRefresh;

  useEffect(() => {
    loadMissionState({ showLoading: true });
  }, [loadMissionState]);

  useEffect(() => {
    // A resume link is a request for the persisted current step, not a forced
    // historical view of the step embedded in an old URL.
    const isResume = ['1', 'true'].includes(String(routeParams?.resume ?? '').toLowerCase());
    if (isResume || !requestedStep) return;
    const nextStepIndex = routeStepIndex({ step: requestedStep });
    if (nextStepIndex >= 0) {
      if (nextStepIndex === stepIndex("brief")) briefEnteredRef.current = true;
      setActiveStep(nextStepIndex);
    }
  }, [requestedStep, routeParams?.resume]);

  useEffect(() => {
    loadConnectorState({
      showLoading: !connectorStateLoadedRef.current,
    });
  }, [loadConnectorState, connectorRefresh]);

  useEffect(() => {
    if (steps[activeStep]?.key !== 'connectors') return;

    loadConnectorCatalog();
    loadMissionState({
      showLoading: false,
      navigateToCurrent: false,
    });
  }, [activeStep, loadConnectorCatalog, loadMissionState]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const refreshAfterFocus = () => {
      if (activeStep < 3 || document.visibilityState !== 'visible') return;

      const now = Date.now();
      if (
        now - lastFocusRefreshAtRef.current <
        CONNECTOR_FOCUS_REFRESH_COOLDOWN_MS
      ) return;

      lastFocusRefreshAtRef.current = now;
      loadConnectorState({ showLoading: false });
      loadMissionState({
        showLoading: false,
        navigateToCurrent: false,
      });
    };

    window.addEventListener('focus', refreshAfterFocus);
    document.addEventListener('visibilitychange', refreshAfterFocus);

    return () => {
      window.removeEventListener('focus', refreshAfterFocus);
      document.removeEventListener('visibilitychange', refreshAfterFocus);
    };
  }, [activeStep, loadConnectorState, loadMissionState]);

  useEffect(() => {
    if (steps[activeStep]?.key !== 'scan' || scanStatus !== 'running') {
      return undefined;
    }

    const timer = window.setInterval(() => {
      pollScanStatus();
    }, SCAN_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [activeStep, pollScanStatus, scanStatus]);

  const toggleConnector = (key) => {
    // Connector selection is an explicit Mission Launch choice. Keep the local
    // choice authoritative until the Connectors step is submitted so background
    // mission-state refreshes cannot erase it while setup is in progress.
    connectorSelectionDirtyRef.current = true;
    setConnectors((current) => current.map((connector) => (
      connector.key === key
        ? { ...connector, selected: connector.selected !== true }
        : connector
    )));
  };

  const authorizeConnector = (key) => {
    const connector = connectors.find((item) => item.key === key);
    const provider = connector?.provider || key;
    openConnectorSetup(provider);
  };

  const selectedConnectors = connectors.filter((connector) => connector.selected === true);
  const connectedConnectorCount = selectedConnectors.filter((connector) => (
    ['healthy', 'connected'].includes(connector.lifecycle_state)
  )).length;
  const selectedConnectorCount = selectedConnectors.length;
  const canContinueFromConnectors = selectedConnectorCount > 0 && connectedConnectorCount > 0;

  const startLaunchTransition = () => {
    setLaunchFinalizing(true);
  };

  const renderStep = () => {
    switch (steps[activeStep]?.key) {
      case 'welcome':
        return <WelcomeMission onBegin={handleWelcomeBegin} />;
      case 'organization':
        return (
          <OrganizationSetup
            organization={organization}
            onChange={setOrganization}
            onSubmit={handleOrganizationSubmit}
            saving={missionSaving}
          />
        );
      case 'frameworks':
        return (
          <FrameworkIntelligence
            intelligence={frameworkIntelligence}
            onSubmit={handleFrameworkSubmit}
            saving={missionSaving}
          />
        );
      case 'connectors':
        return (
          <div className="mission-step-shell connector-launch-step">
            <MissionCard
              eyebrow="Phase 3 · Connector Launch"
              title="Connect the systems that prove operational trust"
              description="OASYS reads live connector status for this tenant. Choose the systems to include, complete any required setup here, then launch the first mission scan."
            >
              <div className="connector-launch-trust-banner" role="note" aria-label="Connector launch guardrail">
                <div>
                  <span>Read-only launch plan</span>
                  <strong>OASYS will collect evidence only. It will not modify source systems.</strong>
                </div>
                <div className="connector-launch-legend" aria-label="Connector status legend">
                  <span><b>Not connected</b> Administrator approval required</span>
                  <span><b>Healthy</b> Ready for Mission Scan</span>
                </div>
              </div>

              {connectorError ? (
                <div className="connector-launch-error" role="alert">
                  <strong>Connector status unavailable.</strong>
                  <span>{connectorError}</span>
                  <button
                    type="button"
                    onClick={() => loadConnectorState({ showLoading: true })}
                    disabled={connectorLoading}
                  >
                    {connectorLoading ? 'Retrying…' : 'Retry'}
                  </button>
                </div>
              ) : null}

              {missionError ? (
                <div className="connector-launch-error" role="alert">
                  <strong>Mission Launch cannot advance yet.</strong>
                  <span>{missionError}</span>
                </div>
              ) : null}

              <div className="connector-launch-summary-row" aria-label="Connector launch summary">
                <span>{selectedConnectorCount} systems selected</span>
                <span>{connectedConnectorCount} connections ready</span>
                <span>{connectors.reduce((total, item) => total + (item.selected === true ? Number(item.estimated_evidence_records || 0) : 0), 0).toLocaleString('en-US')} potential evidence records</span>
                <span>{connectors.reduce((total, item) => total + (item.selected === true ? Number(item.mapped_controls || 0) : 0), 0).toLocaleString('en-US')} mapped controls after authorization</span>
              </div>

              <ConnectorCatalog
                connectors={connectors}
                recommendedKeys={recommendedConnectorKeys}
                loading={connectorLoading}
                onToggle={toggleConnector}
                onAuthorize={authorizeConnector}
              />

              {!canContinueFromConnectors ? (
                <p className="connector-launch-continue-warning">
                  {selectedConnectorCount === 0
                    ? 'Add at least one connector to your Mission Launch plan to continue.'
                    : 'Connect at least one selected provider before running Mission Scan.'}
                </p>
              ) : null}

              <div className="mission-step-actions">
                <button
                  type="button"
                  className="mission-primary-button"
                  onClick={handleConnectorSubmit}
                  disabled={!canContinueFromConnectors || missionSaving}
                >
                  {missionSaving ? 'Confirming Connector Plan…' : 'Confirm Connector Launch Plan'}
                </button>
                <button
                  type="button"
                  className="mission-secondary-button"
                  onClick={() => loadConnectorState({ showLoading: true })}
                  disabled={connectorLoading}
                >
                  {connectorLoading ? 'Refreshing Connector Status…' : 'Refresh Connector Status'}
                </button>
              </div>
            </MissionCard>
          </div>
        );
      case 'scan':
        return (
          <MissionScan
            autoStart={false}
            scanStatus={scanStatus}
            scanRuntime={scanRuntime}
            scanError={scanError}
            scanAttempts={scanAttempts}
            unifiedAwsProgress={unifiedAwsProgress}
            retrying={scanRetrying}
            onStart={startScan}
            onRefresh={pollScanStatus}
            onRetry={retryScan}
            onRescan={rescanScan}
            onComplete={() => advanceLocally('brief')}
            selectedConnectorCount={selectedConnectorCount}
            connectedConnectorCount={connectedConnectorCount}
            stateVersion={scanStateVersion}
            transitionVersion={scanTransitionVersion}
          />
        );
      case 'brief':
        return (
          <>
          <div className="mission-scan-rescan-action">
            <button type="button" className="mission-secondary-button" onClick={rescanScan} disabled={scanStatus !== 'complete'}>
              Run New Mission Scan
            </button>
          </div>
          <ExecutiveMissionBrief
            brief={executiveBrief}
            loading={briefLoading}
            error={briefError}
            saving={missionSaving}
            onRetry={loadExecutiveBrief}
            onContinue={completeExecutiveBrief}
            readOnly={stepStatuses.brief === 'complete'}
          />
          </>
        );
      case 'readiness':
        return (
          <OperationalReadiness
            readiness={readiness}
            loading={readinessLoading}
            error={readinessError}
            saving={missionSaving}
            onRetry={loadOperationalReadiness}
            onContinue={completeOperationalReadiness}
          />
        );
      case 'ready':
        return (
          <MissionReady
            ready={{}}
            error={completionError}
            onLaunch={completeAndLaunchOasys}
            onLaunchStarted={startLaunchTransition}
          />
        );
      default:
        return <WelcomeMission onBegin={handleWelcomeBegin} />;
    }
  };

  return (
    <main
      className={[
        'mission-launch-page',
        isMissionReadyStep ? 'mission-launch-ready-complete' : '',
        launchFinalizing ? 'mission-launch-complete' : '',
      ].filter(Boolean).join(' ')}
      aria-busy={missionLoading}
    >
      <div className="mission-launch-background" aria-hidden="true" />
      <MissionProgress
        steps={steps}
        activeStep={activeStep}
        highestReachedStep={highestReachedStep}
        progress={progress}
        stepStatuses={stepStatuses}
        onStepSelect={goToStep}
      />
      <section className="mission-launch-stage" ref={stageRef}>{renderStep()}</section>

      {connectorSetupOpen ? (
        <ConnectorSetupWizard
          catalog={connectorSetupCatalog}
          loading={connectorSetupLoading}
          error={connectorSetupError}
          users={connectorSetupUsers}
          actions={connectorSetupActions}
          saving={connectorSetupSaving}
          initialProviderKey={connectorSetupProviderKey}
          reconnectMode={connectorSetupReconnectMode}
          embeddedContext="mission-launch"
          onRefresh={() => loadConnectorState({ showLoading: false })}
          onClose={closeConnectorSetup}
        />
      ) : null}
    </main>
  );
}
